/**
 * Global object.
 */

//
//	Requirements
//

import {base64GzipToString} from './base64';
import {z} from 'zod';

//
//	Types
//

const z$globalObjectBaseType = z.strictObject({
	session: z.unknown(),
	moduleOptions: z.record(z.string(), z.unknown()).optional(),
	__unittest__: z.boolean().optional(),
	__useproxy__: z.boolean().optional(),
	__playwright__: z.boolean().optional(),
});

/** @public */
export type globalObjectBaseType = z.infer<typeof z$globalObjectBaseType>;

//
//	Constants
//

export const globalObjectElementId = 'data-global';

//
//	Cache
//

let _globalObject: globalObjectBaseType | null = null;

//
//	API
//

/**
 * Decode global object stored in given script tag.
 * @param encodedContent Text to decode
 */
export const decodeGlobalObject = (encodedContent: string): globalObjectBaseType => {
	const decodedContent = base64GzipToString(encodedContent);
	let json: unknown;
	try {
		json = JSON.parse(decodedContent);
	} catch {
		throw new Error(`Unable to parse "${decodedContent}"}`);
	}

	return z$globalObjectBaseType.parse(json);
};

/**
 * load the global object from the document.
 */
const loadGlobalObject = (): globalObjectBaseType => {
	const el = document.getElementById(globalObjectElementId);
	if (!(el instanceof HTMLScriptElement)) {
		throw new Error(`Script element with id "${globalObjectElementId}" not found`);
	}

	return decodeGlobalObject(el.textContent);
};

/**
 * Get the global object by first looking if we already loaded it or else load it from the document.
 */
export const getGlobalObject = (): globalObjectBaseType => {
	if (_globalObject === null) {
		_globalObject = loadGlobalObject();
	}

	return _globalObject;
};

/**
 * Set the global object.
 * @param globalObject Global object to set.
 */
export const setGlobalObject = (globalObject: globalObjectBaseType): void => {
	_globalObject = globalObject;
};
